// src/pages/VolpeTrackTermsConditions.js

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiGlobe, FiChevronLeft } from 'react-icons/fi';
import SEO from '../components/SEO';

// Styled components
const PageContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(135deg, #1A365D 0%, #0B1D3A 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(135deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(225deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(315deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(45deg,  rgba(255,255,255,0.03) 25%, transparent 25%);
    background-size: 60px 60px;
    opacity: 0.4;
    z-index: 0;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
  width: 100%;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-xl);
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: var(--spacing-xl) var(--spacing-md);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(135deg, rgba(255,255,255,0.05) 25%, transparent 25%),
      linear-gradient(225deg, rgba(255,255,255,0.05) 25%, transparent 25%),
      linear-gradient(315deg, rgba(255,255,255,0.05) 25%, transparent 25%),
      linear-gradient(45deg,  rgba(255,255,255,0.05) 25%, transparent 25%);
    background-size: 60px 60px;
    opacity: 0.5;
    z-index: 0;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--spacing-md);
  color: var(--white);
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastUpdated = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  margin-bottom: var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const TermsContainer = styled(motion.div)`
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(4px);

  @media (max-width: 768px) {
    padding: var(--spacing-lg);
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-top: var(--spacing-lg);
  margin-bottom: var(--spacing-md);
  color: var(--secondary);
  border-bottom: 2px solid var(--accent);
  padding-bottom: 0.5rem;

  &:first-of-type {
    margin-top: 0;
  }
`;

const TermsText = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: var(--spacing-md);
`;

const List = styled.ul`
  margin-bottom: var(--spacing-md);
  padding-left: var(--spacing-lg);
`;

const ListItem = styled.li`
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: var(--spacing-xs);
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  cursor: pointer;
  background: none;
  border: none;
  text-decoration: none;
  background-color: rgba(0, 0, 0, 0.2);
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-sm);
  backdrop-filter: blur(8px);
  transition: all var(--transition-fast);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 10;

  svg { margin-right: var(--spacing-xs); }

  &:hover {
    color: var(--white);
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const LanguageToggle = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: var(--border-radius-md);
  padding: 0.5rem 0.75rem;
  box-shadow: var(--shadow-sm);
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all var(--transition-fast);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 5;

  &:hover {
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: var(--shadow-md);
  }
`;

const LanguageIcon = styled.div`
  margin-right: var(--spacing-xs);
  display: flex;
  align-items: center;
  color: var(--white);
`;

const LanguageText = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--white);
`;

const Strong = styled.strong`
  color: var(--secondary);
`;

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const VolpeTrackTermsConditions = () => {
  const lastUpdated = "Oct 25, 2025";
  const [language, setLanguage] = useState('en');

  const toggleLanguage = () => setLanguage(language === 'en' ? 'es' : 'en');

  const handleBackClick = () => {
    window.location.href = '/';
  };

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <SEO routeKey="volpeTrackTerms" />
      <PageContainer>
        <ContentWrapper>
        <TopBar>
          <BackButton onClick={handleBackClick}>
            <FiChevronLeft /> {language === 'en' ? 'Back to Home' : 'Volver al inicio'}
          </BackButton>
          <LanguageToggle onClick={toggleLanguage}>
            <LanguageIcon><FiGlobe /></LanguageIcon>
            <LanguageText>{language === 'en' ? 'Español' : 'English'}</LanguageText>
          </LanguageToggle>
        </TopBar>

        <PageHeader>
          <PageTitle>
            {language === 'en' ? 'VolpeTrack Terms & Conditions' : 'Términos y Condiciones de VolpeTrack'}
          </PageTitle>
          <LastUpdated>
            {language === 'en'
              ? `Last updated: ${lastUpdated}`
              : `Última actualización: ${lastUpdated}`}
          </LastUpdated>
        </PageHeader>

        {language === 'en' ? (
          <TermsContainer initial="hidden" animate="visible" variants={fadeIn}>
            <TermsText>
              Service provider: <Strong>XADevs LLC</Strong> ("VolpeTrack", "we", "us").<br/>
              Contact: <Strong>support@xadevs.com</Strong> · <Strong>privacy@xadevs.com</Strong> · <Strong>https://xadevs.com</Strong>
            </TermsText>
            <TermsText>
              By using the VolpeTrack mobile app and its related services (the "App"), you agree to these Terms and Conditions ("Terms"). If you do not agree, do not use the App.
            </TermsText>

            <SectionTitle>1. What is VolpeTrack</SectionTitle>
            <TermsText>
              VolpeTrack is a personal finance management app to record income and expenses, visualize habits, set goals, and receive AI assistance for more informed decision-making. It is not a financial advisory, tax, or investment service (see §10).
            </TermsText>

            <SectionTitle>2. Eligibility and Account</SectionTitle>
            <TermsText>
              You must be at least 18 years old (or the age of majority in your country if higher) to use the App. Access is granted via Google Sign-In, Apple Sign-In, or email + password. You are responsible for maintaining the confidentiality of your credentials and for all activity performed from your account.
            </TermsText>

            <SectionTitle>3. Plans and Tiers</SectionTitle>
            <TermsText>We offer three plans:</TermsText>
            <List>
              <ListItem><Strong>Free (Ad-Supported)</Strong>: essential features with advertising.</ListItem>
              <ListItem><Strong>Premium Pro (subscription)</Strong>: advanced benefits without advertising.</ListItem>
              <ListItem><Strong>Founder Pioneer (lifetime)</Strong>: lifetime Premium access (while the VolpeTrack service is operational), ad-free, limited to 200 slots. Non-transferable.</ListItem>
            </List>
            <TermsText>
              Features may vary by region, version, or device. See the store and in-app documentation for current details.
            </TermsText>

            <SectionTitle>4. Purchases, Billing, and Renewals</SectionTitle>
            <TermsText>
              Purchases are managed by Google Play and/or App Store (via RevenueCat). We do not store card data. Charges may appear as <Strong>"XADevs LLC"</Strong>. Subscriptions automatically renew at the end of each period unless canceled beforehand from your store account.
            </TermsText>
            <TermsText>
              Cancellations and refunds are governed by store policies and applicable law. We may adjust prices, currencies, or taxes; we will notify you in advance for active subscriptions, and the change will apply to the next billing cycle.
            </TermsText>

            <SectionTitle>5. Advertising and Consent (Free plan only)</SectionTitle>
            <TermsText>
              In the Free plan, the App shows <Strong>NON-PERSONALIZED</Strong> ads through Unity Ads, AppLovin MAX, and/or Google AdMob. We do not use your personal data to personalize advertising. Ad partners may use the device advertising identifier and basic device/app signals for frequency capping, measurement, and fraud prevention only.
            </TermsText>
            <TermsText>
              Ad SDKs may access the device advertising identifier (GAID/IDFA) for non-personalized ad delivery, frequency capping, measurement, and fraud prevention. We do not use it for interest-based personalization.
            </TermsText>
            <TermsText>
              We do not enable cross-app tracking for advertising. On iOS, App Tracking Transparency (ATT) will only be requested if required by Apple policies. In the EEA/UK, if any consent is required for specific ad SDK operations, we will request it in-app. Premium Pro and Founder Pioneer are ad-free and disable ad SDKs.
            </TermsText>

            <SectionTitle>6. Pre-release & Testing</SectionTitle>
            <TermsText>
              From time to time we may provide pre-release builds to invited testers (e.g., closed testing on Google Play). Such builds may contain diagnostics, logging and A/B flags to help us identify issues. Features may be unstable and subject to change. Testers agree not to redistribute the builds or disclose non-public features.
            </TermsText>

            <SectionTitle>7. Acceptable Use</SectionTitle>
            <TermsText>You agree not to:</TermsText>
            <List>
              <ListItem>Violate the law or third-party rights.</ListItem>
              <ListItem>Manipulate data, metrics, or ads.</ListItem>
              <ListItem>Attempt unauthorized access to systems or perform reverse engineering.</ListItem>
              <ListItem>Introduce malware or illicit content.</ListItem>
            </List>

            <SectionTitle>8. Your Content and Limited License</SectionTitle>
            <TermsText>
              The data you provide (e.g., transactions, notes) is yours. You grant us a limited, non-exclusive, and revocable license to host, process, and display that content to provide the service and improve its operation (including AI-based categorization), in accordance with the Privacy Policy.
            </TermsText>

            <SectionTitle>9. AI Features and Accuracy</SectionTitle>
            <TermsText>
              AI provides estimates and may err. Classifications, insights, and responses are informational and depend on the quality of the data provided. You can edit or review your data and categories at any time.
            </TermsText>

            <SectionTitle>10. Data Export and Deletion</SectionTitle>
            <TermsText>
              The App offers options to export and delete your information. Timeframes and technical details are described in the Privacy Policy (backups, logs, etc.).
            </TermsText>

            <SectionTitle>11. No Advisory Services</SectionTitle>
            <TermsText>
              VolpeTrack does not provide financial, tax, accounting, or investment advice. The information is educational/informational. Consult qualified professionals before making relevant decisions.
            </TermsText>

            <SectionTitle>12. No Warranties</SectionTitle>
            <TermsText>
              The App is provided "as is" and "as available." To the maximum extent permitted by law, we do not guarantee:
            </TermsText>
            <List>
              <ListItem>Accuracy or timeliness of information;</ListItem>
              <ListItem>Uninterrupted availability;</ListItem>
              <ListItem>Compatibility with all devices;</ListItem>
              <ListItem>Specific results.</ListItem>
            </List>

            <SectionTitle>13. Limitation of Liability</SectionTitle>
            <TermsText>
              To the extent permitted by law, XADevs LLC and its affiliates shall not be liable for indirect, incidental, special, punitive, or consequential damages (including loss of profits, data, or savings) arising from the use or inability to use the App.
            </TermsText>
            <TermsText>
              Our total liability for any claim related to the App shall not exceed (a) the amounts you paid in the twelve (12) months prior to the event, or (b) 50 USD if you have not paid any amount, whichever is greater.
            </TermsText>
            <TermsText>
              Nothing herein excludes or limits liabilities that cannot be excluded by law (e.g., mandatory consumer rights).
            </TermsText>

            <SectionTitle>14. Third-Party Services</SectionTitle>
            <TermsText>
              The App integrates third-party services (e.g., Firebase, RevenueCat, OpenAI/Azure/DeepSeek, Bunny.net, Linode, Microsoft Azure, ad networks in the Free plan). Their use may be governed by each third party's terms and policies. We are not responsible for the content or availability of such external services.
            </TermsText>

            <SectionTitle>15. Service Availability and Changes</SectionTitle>
            <TermsText>
              We may improve, modify, suspend, or discontinue features for technical, legal, or operational reasons. If a substantial change adversely affects you in an active subscription, you may cancel it before the effective date.
            </TermsText>

            <SectionTitle>16. Intellectual Property</SectionTitle>
            <TermsText>
              The App (code, design, trademarks, content, except your data) is the property of XADevs LLC or its licensors. You are granted a limited, non-exclusive, non-transferable license to use the App in accordance with these Terms.
            </TermsText>

            <SectionTitle>17. International Compliance and Local Laws</SectionTitle>
            <TermsText>
              These Terms are designed for users in multiple countries. Nothing in them is intended to reduce the mandatory rights granted to you by consumer protection or privacy laws in your place of residence. Where local law requires it, those provisions will prevail over conflicting clauses in these Terms.
            </TermsText>

            <SectionTitle>18. Governing Law and Competent Courts (no arbitration)</SectionTitle>
            <TermsText>
              Unless your country's law provides otherwise, these Terms are governed by the laws of the State of Delaware, United States. Disputes shall be submitted to the competent courts of Delaware or to the courts of your place of residence when required by applicable consumer law. (No arbitration; no waiver of class action.)
            </TermsText>

            <SectionTitle>19. Changes to these Terms</SectionTitle>
            <TermsText>
              We may update these Terms. We will notify significant changes in the App and/or by email. The date at the top indicates the last revision. Continued use after the effective date implies acceptance of the changes.
            </TermsText>

            <SectionTitle>20. Contact</SectionTitle>
            <TermsText>
              Support: <Strong>support@xadevs.com</Strong><br/>
              Privacy: <Strong>privacy@xadevs.com</Strong><br/>
              Website: <Strong>https://xadevs.com</Strong>
            </TermsText>
          </TermsContainer>
        ) : (
          <TermsContainer initial="hidden" animate="visible" variants={fadeIn}>
            <TermsText>
              Titular del servicio: <Strong>XADevs LLC</Strong> ("VolpeTrack", "nosotros").<br/>
              Contacto: <Strong>support@xadevs.com</Strong> · <Strong>privacy@xadevs.com</Strong> · <Strong>https://xadevs.com</Strong>
            </TermsText>
            <TermsText>
              Al usar la aplicación móvil VolpeTrack y sus servicios relacionados (la "App"), aceptas estos Términos y Condiciones ("Términos"). Si no estás de acuerdo, no uses la App.
            </TermsText>

            <SectionTitle>1. Qué es VolpeTrack</SectionTitle>
            <TermsText>
              VolpeTrack es una app de gestión financiera personal para registrar ingresos/gastos, visualizar hábitos, fijar metas y recibir asistencia con IA para tomar decisiones mejor informadas. No es un servicio de asesoramiento financiero, fiscal o de inversión (ver §10).
            </TermsText>

            <SectionTitle>2. Elegibilidad y cuenta</SectionTitle>
            <TermsText>
              Debes tener al menos 18 años (o la mayoría de edad en tu país si es superior) para usar la App. Acceso mediante Google Sign-In, Apple Sign-In o email + contraseña. Eres responsable de mantener la confidencialidad de tus credenciales y de la actividad realizada desde tu cuenta.
            </TermsText>

            <SectionTitle>3. Modalidades y planes</SectionTitle>
            <TermsText>Ofrecemos tres planes:</TermsText>
            <List>
              <ListItem><Strong>Gratuito (Ad-Supported)</Strong>: funciones esenciales con publicidad.</ListItem>
              <ListItem><Strong>Premium Pro (suscripción)</Strong>: ventajas avanzadas sin publicidad.</ListItem>
              <ListItem><Strong>Founder Pioneer (lifetime)</Strong>: acceso Premium de por vida (mientras el servicio VolpeTrack esté operativo), sin publicidad, edición limitada a 200 plazas. No es transferible.</ListItem>
            </List>
            <TermsText>
              Las funcionalidades pueden variar por región, versión o dispositivo. Ver tienda y documentación in-app para el detalle actual.
            </TermsText>

            <SectionTitle>4. Compras, facturación y renovaciones</SectionTitle>
            <TermsText>
              Las compras se gestionan por Google Play y/o App Store (a través de RevenueCat). No almacenamos datos de tarjeta. Los cargos pueden aparecer como <Strong>"XADevs LLC"</Strong>. Las suscripciones se renuevan automáticamente al final de cada período salvo cancelación previa desde tu cuenta de tienda.
            </TermsText>
            <TermsText>
              Cancelaciones y reembolsos: se rigen por las políticas de la tienda y la ley aplicable. Podemos ajustar precios, monedas o impuestos; te avisaremos con antelación razonable para suscripciones vigentes y el cambio aplicará al siguiente ciclo.
            </TermsText>

            <SectionTitle>5. Publicidad y consentimiento (solo plan gratuito)</SectionTitle>
            <TermsText>
              En el plan Gratuito, la App muestra anuncios <Strong>NO PERSONALIZADOS</Strong> a través de Unity Ads, AppLovin MAX y/o Google AdMob. No usamos tus datos personales para personalizar publicidad. Los socios publicitarios pueden usar el identificador de publicidad del dispositivo y señales básicas de dispositivo/app únicamente para limitación de frecuencia, medición y prevención de fraude.
            </TermsText>
            <TermsText>
              Los SDKs de anuncios pueden acceder al identificador de publicidad del dispositivo (GAID/IDFA) para entrega de anuncios no personalizados, limitación de frecuencia, medición y prevención de fraude. No lo usamos para personalización basada en intereses.
            </TermsText>
            <TermsText>
              No habilitamos el seguimiento entre aplicaciones con fines publicitarios. En iOS, App Tracking Transparency (ATT) solo se solicitará si lo requieren las políticas de Apple. En el EEE/Reino Unido, si se requiere algún consentimiento para operaciones específicas de SDK de anuncios, lo solicitaremos en la app. Premium Pro y Founder Pioneer no tienen publicidad y deshabilitan los SDK de anuncios.
            </TermsText>

            <SectionTitle>6. Pre-lanzamiento y pruebas</SectionTitle>
            <TermsText>
              De vez en cuando podemos proporcionar versiones de pre-lanzamiento a testers invitados (p. ej., pruebas cerradas en Google Play). Dichas versiones pueden contener diagnósticos, registro y banderas A/B para ayudarnos a identificar problemas. Las funciones pueden ser inestables y sujetas a cambios. Los testers aceptan no redistribuir las versiones ni divulgar funciones no públicas.
            </TermsText>

            <SectionTitle>7. Uso aceptable</SectionTitle>
            <TermsText>Te comprometes a no:</TermsText>
            <List>
              <ListItem>Infringir la ley o derechos de terceros.</ListItem>
              <ListItem>Manipular datos, métricas o anuncios.</ListItem>
              <ListItem>Intentar acceder indebidamente a sistemas o realizar ingeniería inversa.</ListItem>
              <ListItem>Introducir malware o contenido ilícito.</ListItem>
            </List>

            <SectionTitle>8. Tu contenido y licencia limitada</SectionTitle>
            <TermsText>
              Los datos que aportas (p. ej., transacciones, notas) son tuyos. Nos concedes una licencia limitada, no exclusiva y revocable para alojar, procesar y mostrar ese contenido a fin de prestar el servicio y mejorar su funcionamiento (incluida la categorización por IA), conforme a la Política de Privacidad.
            </TermsText>

            <SectionTitle>9. Funciones de IA y precisión</SectionTitle>
            <TermsText>
              La IA ofrece estimaciones y puede errar. Las clasificaciones, insights y respuestas son informativos y dependen de la calidad de los datos aportados. Puedes editar o revisar tus datos y categorías en cualquier momento.
            </TermsText>

            <SectionTitle>10. Exportación y eliminación de datos</SectionTitle>
            <TermsText>
              La App ofrece opciones para exportar y eliminar tu información. Los plazos y detalles técnicos se describen en la Política de Privacidad (copias de seguridad, logs, etc.).
            </TermsText>

            <SectionTitle>11. Aviso de no-asesoramiento</SectionTitle>
            <TermsText>
              VolpeTrack no presta asesoramiento financiero, fiscal, contable ni de inversión. La información es educativa/informativa. Consulta a profesionales cualificados antes de tomar decisiones relevantes.
            </TermsText>

            <SectionTitle>12. Sin garantías</SectionTitle>
            <TermsText>
              La App se proporciona "tal cual" y "según disponibilidad". En la máxima medida permitida por la ley, no garantizamos:
            </TermsText>
            <List>
              <ListItem>Exactitud o actualidad de la información;</ListItem>
              <ListItem>Disponibilidad ininterrumpida;</ListItem>
              <ListItem>Compatibilidad con todos los dispositivos;</ListItem>
              <ListItem>Resultados concretos.</ListItem>
            </List>

            <SectionTitle>13. Limitación de responsabilidad</SectionTitle>
            <TermsText>
              En la medida permitida por la ley, XADevs LLC y sus afiliados no serán responsables de daños indirectos, incidentales, especiales, punitivos o consecuentes (incluida pérdida de beneficios, datos o ahorros) derivados del uso o imposibilidad de uso de la App.
            </TermsText>
            <TermsText>
              Nuestra responsabilidad total por cualquier reclamación relacionada con la App no excederá (a) las cantidades pagadas por ti en los doce (12) meses previos al evento, o (b) 50 USD si no hubieras pagado importe alguno, lo que sea mayor.
            </TermsText>
            <TermsText>
              Nada de lo anterior excluye o limita responsabilidades que no puedan excluirse por ley (p. ej., derechos imperativos de consumidores).
            </TermsText>

            <SectionTitle>14. Servicios de terceros</SectionTitle>
            <TermsText>
              La App integra servicios de terceros (p. ej., Firebase, RevenueCat, OpenAI/Azure/DeepSeek, Bunny.net, Linode, Microsoft Azure, redes de anuncios en el plan gratuito). Su uso puede regirse por términos y políticas de cada tercero. No somos responsables del contenido o disponibilidad de dichos servicios externos.
            </TermsText>

            <SectionTitle>15. Disponibilidad y cambios del servicio</SectionTitle>
            <TermsText>
              Podemos mejorar, modificar, suspender o retirar funciones por motivos técnicos, legales u operativos. Si un cambio sustancial te perjudica en una suscripción vigente, podrás cancelarla antes de la fecha de efecto.
            </TermsText>

            <SectionTitle>16. Propiedad intelectual</SectionTitle>
            <TermsText>
              La App (código, diseño, marcas, contenidos, salvo tus datos) es propiedad de XADevs LLC o sus licenciantes. Se te concede una licencia limitada, no exclusiva e intransferible para usar la App conforme a estos Términos.
            </TermsText>

            <SectionTitle>17. Cumplimiento internacional y leyes locales</SectionTitle>
            <TermsText>
              Estos Términos están pensados para usuarios en múltiples países. Nada en ellos pretende reducir los derechos imperativos que te otorguen las leyes de protección al consumidor o privacidad de tu lugar de residencia. Cuando la ley local lo exija, esas disposiciones prevalecerán sobre cláusulas en conflicto de estos Términos.
            </TermsText>

            <SectionTitle>18. Legislación aplicable y tribunales competentes (sin arbitraje)</SectionTitle>
            <TermsText>
              Salvo que la ley de tu país disponga otra cosa, estos Términos se rigen por las leyes del Estado de Delaware, Estados Unidos. Las controversias se someterán a los tribunales competentes de Delaware o a los tribunales de tu lugar de residencia cuando así lo exija la ley de consumo aplicable. (Sin arbitraje; sin renuncia a acción colectiva.)
            </TermsText>

            <SectionTitle>19. Cambios a estos Términos</SectionTitle>
            <TermsText>
              Podemos actualizar estos Términos. Notificaremos cambios significativos en la App y/o por email. La fecha al inicio indica la última revisión. El uso continuado tras la entrada en vigor implica aceptación de los cambios.
            </TermsText>

            <SectionTitle>20. Contacto</SectionTitle>
            <TermsText>
              Soporte: <Strong>support@xadevs.com</Strong><br/>
              Privacidad: <Strong>privacy@xadevs.com</Strong><br/>
              Web: <Strong>https://xadevs.com</Strong>
            </TermsText>
          </TermsContainer>
          )}
        </ContentWrapper>
      </PageContainer>
    </>
  );
};

export default VolpeTrackTermsConditions;
